import type { PlaceOrderInput } from '../api/types'
import {
  CHECKOUT_SCHEDULE_MODE,
  CHECKOUT_SPLIT_MODE,
  SCHEDULE_IST_OFFSET,
  SCHEDULE_EXPRESS_MAX_DAYS_AHEAD,
  SCHEDULE_EXPRESS_MIN_DAYS_AHEAD,
  SCHEDULE_KIND,
  SCHEDULE_MAX_DAYS_AHEAD,
  SCHEDULE_MIN_DAYS_AHEAD,
  SCHEDULE_MIN_LEAD_MINUTES,
  SCHEDULE_TIME_ZONE,
  SCHEDULE_UI,
  SCHEDULE_WINDOW_END_HOUR,
  SCHEDULE_WINDOW_START_HOUR,
  type CheckoutScheduleMode,
  type CheckoutSplitMode,
  type ScheduleDateOption,
  type ScheduleKind,
  type ScheduleSlotOption,
} from '../constants/schedule'
import { range } from './range'

const DATE_KEY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: SCHEDULE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-IN', {
  timeZone: SCHEDULE_TIME_ZONE,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

const TIME_FORMATTER = new Intl.DateTimeFormat('en-IN', {
  timeZone: SCHEDULE_TIME_ZONE,
  hour: 'numeric',
  minute: '2-digit',
})

const SLOT_START_HOURS = range(
  SCHEDULE_WINDOW_END_HOUR - SCHEDULE_WINDOW_START_HOUR,
).map((offset) => SCHEDULE_WINDOW_START_HOUR + offset)

export function istDateKey(date: Date): string {
  return DATE_KEY_FORMATTER.format(date)
}

export function slotStartIso(dateKey: string, hour: number): string {
  return `${dateKey}T${String(hour).padStart(2, '0')}:00:00${SCHEDULE_IST_OFFSET}`
}

export function formatSlotRange(iso: string): string {
  const start = new Date(iso)
  const end = new Date(start.getTime() + 60 * 60 * 1000)
  return `${TIME_FORMATTER.format(start)} – ${TIME_FORMATTER.format(end)}`
}

export function formatScheduledDelivery(iso: string | null | undefined): string {
  if (!iso) return ''
  const start = new Date(iso)
  return `${SCHEDULE_UI.scheduledPrefix} ${WEEKDAY_FORMATTER.format(start)}, ${TIME_FORMATTER.format(start)}`
}

export function listScheduleDates(
  now = new Date(),
  kind: ScheduleKind = SCHEDULE_KIND.standard,
): ScheduleDateOption[] {
  const todayKey = istDateKey(now)
  const todayNoon = new Date(`${todayKey}T12:00:00${SCHEDULE_IST_OFFSET}`)
  const tomorrowKey = istDateKey(new Date(todayNoon.getTime() + 86_400_000))
  const bounds = scheduleDayBounds(kind)
  return range(bounds.span).flatMap((offset) => {
    const date = new Date(
      todayNoon.getTime() + (offset + bounds.minDaysAhead) * 86_400_000,
    )
    const key = istDateKey(date)
    const slots = listScheduleSlots(key, now, kind)
    if (slots.length === 0) return []
    return [
      {
        key,
        label: scheduleDateLabel(key, todayKey, tomorrowKey),
      },
    ]
  })
}

export function listScheduleSlots(
  dateKey: string,
  now = new Date(),
  kind: ScheduleKind = SCHEDULE_KIND.standard,
): ScheduleSlotOption[] {
  if (kind === SCHEDULE_KIND.standard && dateKey === istDateKey(now)) return []
  const leadMs = SCHEDULE_MIN_LEAD_MINUTES * 60 * 1000
  const earliest = now.getTime() + leadMs
  return SLOT_START_HOURS.flatMap((hour) => {
    const iso = slotStartIso(dateKey, hour)
    if (new Date(iso).getTime() < earliest) return []
    return [
      {
        iso,
        hour,
        label: formatSlotRange(iso),
      },
    ]
  })
}

export function checkoutPlaceFlags(input: {
  canSplit: boolean
  splitMode: CheckoutSplitMode
  scheduleMode: CheckoutScheduleMode
  scheduledFor: string | null
  expressScheduleMode: CheckoutScheduleMode
  expressScheduledFor: string | null
}): Pick<PlaceOrderInput, 'splitOrders' | 'scheduledFor' | 'expressScheduledFor'> {
  return {
    ...(input.canSplit
      ? { splitOrders: input.splitMode === CHECKOUT_SPLIT_MODE.split }
      : {}),
    ...(input.scheduleMode === CHECKOUT_SCHEDULE_MODE.scheduled &&
    input.scheduledFor
      ? { scheduledFor: input.scheduledFor }
      : {}),
    ...(input.expressScheduleMode === CHECKOUT_SCHEDULE_MODE.scheduled &&
    input.expressScheduledFor
      ? { expressScheduledFor: input.expressScheduledFor }
      : {}),
  }
}

function scheduleDayBounds(kind: ScheduleKind): {
  minDaysAhead: number
  span: number
} {
  if (kind === SCHEDULE_KIND.express) {
    return {
      minDaysAhead: SCHEDULE_EXPRESS_MIN_DAYS_AHEAD,
      span: SCHEDULE_EXPRESS_MAX_DAYS_AHEAD - SCHEDULE_EXPRESS_MIN_DAYS_AHEAD + 1,
    }
  }
  return {
    minDaysAhead: SCHEDULE_MIN_DAYS_AHEAD,
    span: SCHEDULE_MAX_DAYS_AHEAD - SCHEDULE_MIN_DAYS_AHEAD + 1,
  }
}

function scheduleDateLabel(
  key: string,
  todayKey: string,
  tomorrowKey: string,
): string {
  if (key === todayKey) return SCHEDULE_UI.todayLabel
  if (key === tomorrowKey) return SCHEDULE_UI.tomorrowLabel
  return WEEKDAY_FORMATTER.format(new Date(`${key}T12:00:00${SCHEDULE_IST_OFFSET}`))
}
